import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import UnstyledImage from "./image";
import { Carousel } from "react-responsive-carousel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { useWindowSize } from "../utils/use-window-size.js";
import { checkDarkMode } from "../utils/helpers.js";

const CarouselWrapper = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;
const CarouselImage = styled(UnstyledImage)`
  height: 100%;
  cursor: zoom-in;
`;
const Wrapper = styled.div`
  position: relative;
  max-height: 609px;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-height: 430px;
  }
  @media (max-width: ${(props) => props.theme.breakpoints.phone}) {
    height: 350px !important;
    max-height: none;
  }
`;
const FrontPageWrapper = styled.div`
  position: relative;
  max-height: 609px;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-height: 609px;
  }
  @media (max-width: ${(props) => props.theme.breakpoints.phone}) {
    height: 90vh !important;
    max-height: none;
  }
`;
const SmallWrapper = styled.div`
  position: relative;
  max-height: 368px;
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    max-height: 195px;
  }
`;

const SlideButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: ${({ theme }) => checkDarkMode("#fff", theme)};
  color: ${({ theme }) => checkDarkMode("#333", theme)};
  height: 50px;
  width: 50px;
  font-size: 1rem;
  z-index: 99;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    top: 45%;
    height: 40px;
    width: 40px;
  }

  &.prev {
    left: 1rem;
    svg {
      margin-right: 3px;
    }
  }
  &.next {
    right: 1rem;
    svg {
      margin-left: 3px;
    }
  }
`;

const SlideIndicator = styled.li`
  height: 4px;
  background: #dbdbdb;
  display: inline-block;
`;
const CloseIconWrapper = styled.div`
  position: absolute;
  left: 1rem;
  top: 1rem;
  z-index: 9999;
  cursor: pointer;
  background-color: var(--color-white);
  height: 50px;
  width: 50px;
  border-radius: var(--border-radius);
  display: flex;
  justify-content: center;
  align-items: center;
`;
const CloseIcon = styled(FontAwesomeIcon)`
  color: var(--color-black);
  font-size: 30px !important;
`;

const FullscreenWrapper = styled.div`
  position: fixed !important;
  z-index: 999;
  background: rgba(0, 0, 0, 0.9);
  height: 100vh;
  width: 100vw;
  left: 0;
  top: 0;
  display: flex;
  justify-content: center;
`;

const InnerWrapper = styled.div`
  cursor: zoom-out;
  margin: auto;
  max-width: 80%;
  position: relative;
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    max-width: 100%;
  }
`;

const FullscreenImage = styled(UnstyledImage)`
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    object-fit: contain !important;
    width: 100% !important;
  }
`;

export default function HeroCarousel({
  mediaItems,
  mediaItemsMobile,
  variant,
  autoPlay,
}) {
  const desktopMedia = React.useMemo(() => mediaItems || [], [mediaItems]);
  const mobileMedia = React.useMemo(
    () => mediaItemsMobile || [],
    [mediaItemsMobile]
  );
  const [media, setMedia] = React.useState(desktopMedia);
  const windowSize = useWindowSize();
  React.useEffect(() => {
    if (windowSize.width) {
      if (windowSize.width <= 600 && mobileMedia.length > 0) {
        setMedia(mobileMedia);
      } else if (windowSize.width <= 600 && mobileMedia.length === 0) {
        setMedia(desktopMedia);
      } else if (windowSize.width >= 600 && desktopMedia.length === 0) {
        setMedia(mobileMedia);
      } else {
        setMedia(desktopMedia);
      }
    }
  }, [windowSize, desktopMedia, mobileMedia]);

  const [activeImage, setActiveImage] = React.useState({});
  const [fullscreen, showFullScreen] = React.useState(false);
  const showFullscreenImage = (image) => {
    setActiveImage(image);
    showFullScreen(true);
  };

  return (
    <CarouselWrapper>
      <Carousel
        autoPlay={autoPlay ? true : false}
        showStatus={false}
        infiniteLoop={true}
        showThumbs={false}
        renderArrowPrev={(onClickHandler, hasPrev, label) =>
          hasPrev && (
            <SlideButton
              onClick={onClickHandler}
              title={label}
              className="prev"
            >
              <FontAwesomeIcon icon={faChevronLeft} size="sm" />
            </SlideButton>
          )
        }
        renderArrowNext={(onClickHandler, hasNext, label) =>
          hasNext && (
            <SlideButton
              onClick={onClickHandler}
              title={label}
              className="next"
            >
              <FontAwesomeIcon icon={faChevronRight} size="sm" />
            </SlideButton>
          )
        }
        renderIndicator={(onClickHandler, isSelected, index, label) => {
          if (isSelected) {
            return (
              <SlideIndicator
                style={{
                  background: "#343434",
                  width: `calc(100%/${media?.length})`,
                }}
                aria-label={`Selected: ${label} ${index + 1}`}
                title={`Selected: ${label} ${index + 1}`}
              />
            );
          }
          return (
            <SlideIndicator
              style={{ width: `calc(100%/${media?.length})` }}
              onClick={onClickHandler}
              onKeyDown={onClickHandler}
              value={index}
              key={index}
              role="button"
              tabIndex={0}
              title={`${label} ${index + 1}`}
              aria-label={`${label} ${index + 1}`}
            />
          );
        }}
      >
        {media?.filter(Boolean).map((slide, index) =>
          variant === "small" ? (
            <SmallWrapper
              key={index}
              className={variant}
              onClick={() => showFullscreenImage(slide)}
            >
              <CarouselImage image={slide} loading="eager" />
            </SmallWrapper>
          ) : variant === "frontpage" ? (
            <FrontPageWrapper
              key={index}
              onClick={() => showFullscreenImage(slide)}
            >
              <CarouselImage image={slide} loading="eager" />
            </FrontPageWrapper>
          ) : (
            <Wrapper
              key={index}
              className={variant}
              onClick={() => showFullscreenImage(slide)}
            >
              <CarouselImage image={slide} loading="eager" />
            </Wrapper>
          )
        )}
      </Carousel>
      {fullscreen && (
        <FullscreenWrapper>
          <InnerWrapper>
            <FullscreenImage
              image={activeImage}
              loading="eager"
              onClick={() => showFullScreen(false)}
            />
            <CloseIconWrapper>
              <CloseIcon
                icon={faXmark}
                size="3x"
                onClick={() => showFullScreen(false)}
              />
            </CloseIconWrapper>
          </InnerWrapper>
        </FullscreenWrapper>
      )}
    </CarouselWrapper>
  );
}

HeroCarousel.propTypes = {
  mediaItems: PropTypes.arrayOf(PropTypes.object),
  mediaItemsMobile: PropTypes.arrayOf(PropTypes.object),
  autoPlay: PropTypes.bool,
  variant: PropTypes.oneOf(["big", "frontpage", "small", "side-by-side"]),
};

HeroCarousel.defaultProps = {
  mediaItems: undefined,
  mediaItemsMobile: undefined,
  variant: "big",
  autoPlay: true,
};
